import { useMemo, useState } from 'react';
import { Col, Flex, Grid, Row, Skeleton } from 'antd';
import { useOneMeterModule } from 'api/hooks/meter-modules/useOneModule';
import { Button, Card, Descriptions, Form, PageHeader } from 'components';
import { useHistory, useParams } from 'react-router-dom';
import { RoleName } from '@enmon/commons/lib/tools';
import { useUpdateMeterModule } from 'api/hooks/meter-modules/useUpdateModule';
import { useUserInfo } from '@/hooks/useUser';
import { useCreateMeterModule } from 'api/hooks/meter-modules/useCreateModule';
import { isNil } from 'ramda';
import { Icon } from 'components/icon';
import { getMeterModuleDescriptionColumns } from './utils/getDescriptionsConfig';
import { MODULE_FORM_ID } from './constants';
import { CompatibleMeterModelsTable } from './components/Table';
import { AssignModuleToModelModal } from './components/Modal';
import { MeterModuleCompatibleModelsFilter } from './components/Filter';
import { ModuleDetailContext } from './store';
import type { MeterModuleFormValues } from './types';
import { MeterModulePhotos } from './components/Photos';
import { MeterModulePdfViewer } from './components/PdfViewer';

const { useBreakpoint } = Grid;

export const ModuleDetailPage = () => {
  const { id: moduleId } = useParams<{ id: string }>();

  const history = useHistory();
  const breakpoints = useBreakpoint();
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;
  const isCreateFormMode = !moduleId;

  // Form
  const [form] = Form.useForm<MeterModuleFormValues>();
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // Data
  const { data: moduleData, isLoading: isLoadingModule } = useOneMeterModule(
    { moduleId: Number(moduleId), populate: ['meter_models'] },
    { enabled: !!moduleId },
  );
  const { mutate: updateModule, isLoading: isUpdatingModule } = useUpdateMeterModule();
  const { mutate: createModule, isLoading: isCreatingModule } = useCreateMeterModule();

  // Context value
  const contextValue = useMemo(
    () => ({
      moduleId: moduleId ? Number(moduleId) : undefined,
      module: moduleData,
    }),
    [moduleId, moduleData],
  );

  // Columns config
  const moduleColumns = getMeterModuleDescriptionColumns();

  // Handlers
  const handleCreateModule = (values: MeterModuleFormValues) => {
    createModule(values, {
      onSuccess: responseData => {
        history.replace(`/reseller-data/model-library/module/${responseData.id}`);
      },
    });
  };

  const handleUpdateModule = (values: MeterModuleFormValues) => {
    if (!moduleId) throw new Error('Module is not defined in update context');

    // Convert undefined to null for DTO compatibility
    const dtoValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, isNil(value) ? null : value]),
    );

    updateModule(
      {
        id: Number(moduleId),
        values: dtoValues,
      },
      {
        onSuccess: () => {
          setIsFormChanged(false);
        },
      },
    );
  };

  if (isLoadingModule || isUpdatingModule || isCreatingModule) {
    return <Skeleton />;
  }

  return (
    <ModuleDetailContext.Provider value={contextValue}>
      <Flex vertical gap={8}>
        <PageHeader
          breadcrumbs={[
            { path: '/reseller-data/model-library/module', breadcrumbName: 'Model library' },
            { path: '', breadcrumbName: isCreateFormMode ? 'Create new module' : moduleData?.name },
          ]}
          extra={
            isMagicRole && (
              <Button
                disabled={!isFormChanged}
                form={MODULE_FORM_ID}
                htmlType="submit"
                loading={isUpdatingModule}
                type="primary"
              >
                Save
              </Button>
            )
          }
          ghost={false}
          title={isCreateFormMode ? 'Create new module' : moduleData?.name}
          onBack={() => history.goBack()}
        />

        <Row gutter={[8, 8]}>
          <Col lg={16} md={24} xl={16} xs={24}>
            <Flex vertical gap={8}>
              <Row gutter={[8, 8]}>
                <Col lg={24} md={12} xl={24} xs={24} xxl={12}>
                  <Card>
                    <Form<MeterModuleFormValues>
                      form={form}
                      id={MODULE_FORM_ID}
                      onFinish={values => (isCreateFormMode ? handleCreateModule(values) : handleUpdateModule(values))}
                      onValuesChange={() => setIsFormChanged(true)}
                    >
                      <Descriptions
                        column={1}
                        columns={moduleColumns}
                        data={moduleData ?? {}}
                        isEditing={isMagicRole}
                        labelWidth={150}
                        layout="vertical"
                        size="small"
                      />
                    </Form>
                  </Card>
                </Col>

                {!isCreateFormMode && (
                  <Col lg={24} md={12} xl={24} xs={24} xxl={12}>
                    <MeterModulePhotos />
                  </Col>
                )}
              </Row>

              {(breakpoints.xs || breakpoints.sm || breakpoints.md) && !breakpoints.lg && !isCreateFormMode && (
                <MeterModulePdfViewer />
              )}

              {moduleId && !isCreateFormMode && (
                <Card
                  title="Compatible meter models"
                  {...(isMagicRole && {
                    extra: (
                      <Button icon={<Icon iconString="plus" />} onClick={() => setIsAssignModalOpen(true)}>
                        Assign to model
                      </Button>
                    ),
                  })}
                >
                  <Flex vertical gap={16}>
                    <MeterModuleCompatibleModelsFilter />

                    <CompatibleMeterModelsTable />
                  </Flex>
                </Card>
              )}

              {/* Assign to model modal */}
              <AssignModuleToModelModal open={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} />
            </Flex>
          </Col>

          {breakpoints.lg && !isCreateFormMode && (
            <Col lg={8} md={24} xl={8} xs={24}>
              <MeterModulePdfViewer />
            </Col>
          )}
        </Row>
      </Flex>
    </ModuleDetailContext.Provider>
  );
};
